import { useState, useEffect } from 'react';

interface AnimatedLogoProps {
  isScrolled?: boolean;
  className?: string;
}

const AnimatedLogo = ({ isScrolled = false, className = '' }: AnimatedLogoProps) => {
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <svg
        width="180"
        height="48"
        viewBox="0 0 180 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-auto h-12"
      >
        {/* L */}
        <path
          d="M8 8 L8 40 L20 40"
          stroke={isScrolled ? '#2D2D2D' : '#FFFFFF'}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isAnimating ? 'animate-draw-line' : ''}
          style={{
            strokeDasharray: isAnimating ? '64' : 'none',
            strokeDashoffset: isAnimating ? '64' : '0',
            animation: isAnimating ? 'drawLine 0.4s ease-out forwards' : 'none',
          }}
        />

        {/* E */}
        <path
          d="M28 8 L28 40 M28 8 L40 8 M28 24 L38 24 M28 40 L40 40"
          stroke={isScrolled ? '#2D2D2D' : '#FFFFFF'}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isAnimating ? 'animate-draw-line' : ''}
          style={{
            strokeDasharray: isAnimating ? '96' : 'none',
            strokeDashoffset: isAnimating ? '96' : '0',
            animation: isAnimating ? 'drawLine 0.4s ease-out 0.1s forwards' : 'none',
          }}
        />

        {/* V */}
        <path
          d="M46 8 L52 40 L58 8"
          stroke={isScrolled ? '#2D2D2D' : '#FFFFFF'}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isAnimating ? 'animate-draw-line' : ''}
          style={{
            strokeDasharray: isAnimating ? '68' : 'none',
            strokeDashoffset: isAnimating ? '68' : '0',
            animation: isAnimating ? 'drawLine 0.4s ease-out 0.2s forwards' : 'none',
          }}
        />

        {/* A */}
        <path
          d="M64 40 L70 8 L76 40 M66 28 L74 28"
          stroke={isScrolled ? '#2D2D2D' : '#FFFFFF'}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isAnimating ? 'animate-draw-line' : ''}
          style={{
            strokeDasharray: isAnimating ? '80' : 'none',
            strokeDashoffset: isAnimating ? '80' : '0',
            animation: isAnimating ? 'drawLine 0.4s ease-out 0.3s forwards' : 'none',
          }}
        />

        {/* N */}
        <path
          d="M82 40 L82 8 L94 40 L94 8"
          stroke={isScrolled ? '#2D2D2D' : '#FFFFFF'}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isAnimating ? 'animate-draw-line' : ''}
          style={{
            strokeDasharray: isAnimating ? '96' : 'none',
            strokeDashoffset: isAnimating ? '96' : '0',
            animation: isAnimating ? 'drawLine 0.4s ease-out 0.4s forwards' : 'none',
          }}
        />

        {/* T */}
        <path
          d="M100 8 L112 8 M106 8 L106 40"
          stroke={isScrolled ? '#2D2D2D' : '#FFFFFF'}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isAnimating ? 'animate-draw-line' : ''}
          style={{
            strokeDasharray: isAnimating ? '56' : 'none',
            strokeDashoffset: isAnimating ? '56' : '0',
            animation: isAnimating ? 'drawLine 0.4s ease-out 0.5s forwards' : 'none',
          }}
        />

        {/* I */}
        <path
          d="M118 8 L118 40"
          stroke={isScrolled ? '#2D2D2D' : '#FFFFFF'}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isAnimating ? 'animate-draw-line' : ''}
          style={{
            strokeDasharray: isAnimating ? '32' : 'none',
            strokeDashoffset: isAnimating ? '32' : '0',
            animation: isAnimating ? 'drawLine 0.4s ease-out 0.6s forwards' : 'none',
          }}
        />

        {/* N */}
        <path
          d="M124 40 L124 8 L136 40 L136 8"
          stroke={isScrolled ? '#2D2D2D' : '#FFFFFF'}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isAnimating ? 'animate-draw-line' : ''}
          style={{
            strokeDasharray: isAnimating ? '96' : 'none',
            strokeDashoffset: isAnimating ? '96' : '0',
            animation: isAnimating ? 'drawLine 0.4s ease-out 0.7s forwards' : 'none',
          }}
        />

        {/* A */}
        <path
          d="M142 40 L148 8 L154 40 M144 28 L152 28"
          stroke={isScrolled ? '#2D2D2D' : '#FFFFFF'}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isAnimating ? 'animate-draw-line' : ''}
          style={{
            strokeDasharray: isAnimating ? '80' : 'none',
            strokeDashoffset: isAnimating ? '80' : '0',
            animation: isAnimating ? 'drawLine 0.4s ease-out 0.8s forwards' : 'none',
          }}
        />

        {/* Orange accent line - builds last */}
        <path
          d="M8 44 L154 44"
          stroke="#FF6B35"
          strokeWidth="2"
          strokeLinecap="round"
          className={isAnimating ? 'animate-draw-line' : ''}
          style={{
            strokeDasharray: isAnimating ? '146' : 'none',
            strokeDashoffset: isAnimating ? '146' : '0',
            animation: isAnimating ? 'drawLine 0.6s ease-out 0.9s forwards' : 'none',
          }}
        />
      </svg>

      <style>{`
        @keyframes drawLine {
          to {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AnimatedLogo;
